import { useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FieldInput } from "@/components/FieldInput";
import { api, errMsg, inr, todayISO } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useMasters } from "@/hooks/useMasters";

const DSE_FIELD = { name: "dse_id", label: "DSE Name", type: "dse", required: true };
const blank = () => ({ dse_id: "", amount: "", transaction_date: todayISO(), remarks: "" });

export function AllocateDialog({ row, onClose }) {
  const masters = useMasters();
  const { bump } = useAuth();
  const [rows, setRows] = useState([blank()]);
  const [busy, setBusy] = useState(false);
  const total = rows.reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const remaining = Math.round((row._rz_unallocated - total) * 100) / 100;
  const set = (i, k, v) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const save = async () => {
    if (remaining < 0) return toast.error("Allocation unallocated amount se zyada hai");
    setBusy(true);
    try {
      const { data } = await api.post(`/razorpay/${row._txn}/allocate`, { allocations: rows });
      toast.success(`Allocated: ${data.transaction_ids.join(", ")}`);
      bump();
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="rz-allocate-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">DSE-wise Allocation — {row._txn} ({row.reference_no})</DialogTitle>
          <DialogDescription className="text-xs">Bank mein amount pehle hi credit ho chuka hai. Allocation se sirf DSE outstanding kam hoga, bank balance dobara nahi badhega.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {rows.map((r, i) => (
            <div key={i} className="grid grid-cols-1 sm:grid-cols-[1.4fr_1fr_1fr_1.2fr_auto] gap-2 items-end" data-testid={`rz-row-${i}`}>
              <FieldInput f={DSE_FIELD} value={r.dse_id} values={r} masters={masters} testPrefix={`rz-${i}`} onChange={(v) => set(i, "dse_id", v)} />
              <Input type="number" placeholder="Amount ₹" className="num" value={r.amount} onChange={(e) => set(i, "amount", e.target.value)} data-testid={`rz-${i}-amount`} />
              <Input type="date" className="num" value={r.transaction_date} onChange={(e) => set(i, "transaction_date", e.target.value)} data-testid={`rz-${i}-date`} />
              <Input placeholder="Remarks" value={r.remarks} onChange={(e) => set(i, "remarks", e.target.value)} data-testid={`rz-${i}-remarks`} />
              <Button variant="ghost" size="icon" onClick={() => setRows((rs) => rs.filter((_, j) => j !== i))} disabled={rows.length === 1}><Trash2 className="h-4 w-4" /></Button>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={() => setRows((rs) => [...rs, blank()])} data-testid="rz-add-row"><Plus className="h-4 w-4" /> Add DSE</Button>
          <div className="grid grid-cols-3 gap-2 text-sm rounded-lg border bg-slate-50 p-3 num" data-testid="rz-summary">
            <span>Unallocated: <b>{inr(row._rz_unallocated)}</b></span>
            <span>This allocation: <b>{inr(total)}</b></span>
            <span className={remaining < 0 ? "text-red-600" : ""}>Remaining: <b>{inr(remaining)}</b></span>
          </div>
        </div>
        <DialogFooter><Button onClick={save} disabled={busy} data-testid="rz-allocate-save">{busy ? "Saving..." : "Save allocation"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
